import React, { useEffect, useState } from 'react';

import type { LoadingConfig } from '../index';

import {
  ContentWrapper,
  ContextualLoadingContainer,
  MessageText,
  MessageTextContainer,
} from './styledComponents';

interface ContextualLoadingViewProps {
  config: LoadingConfig;
}

const FADE_DURATION = 300;
const DEFAULT_INTERVAL = 2000;

function getRandomTextIndex(currentIndex: number, totalCount: number): number {
  if (totalCount <= 1) return 0;
  const next = Math.floor(Math.random() * totalCount);
  return next === currentIndex ? (currentIndex + 1) % totalCount : next;
}

const ContextualLoadingView: React.FC<ContextualLoadingViewProps> = ({
  config
}: ContextualLoadingViewProps) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const texts = config.texts || [];
  const shouldRotateTexts = texts.length > 1;
  const textToRender = texts[currentTextIndex] || '';

  useEffect(() => {
    if (!shouldRotateTexts) {
      return undefined;
    }

    const configuredInterval = config.interval || DEFAULT_INTERVAL;
    let fadeTimeout: ReturnType<typeof setTimeout> | undefined;
    const textInterval = setInterval(() => {
      setIsFadingOut(true);
      fadeTimeout = setTimeout(() => {
        setCurrentTextIndex((prevTextIndex) =>
          getRandomTextIndex(prevTextIndex, texts.length)
        );
        setIsFadingOut(false);
      }, FADE_DURATION);
    }, configuredInterval);

    return () => {
      clearInterval(textInterval);
      if (fadeTimeout) {
        clearTimeout(fadeTimeout);
      }
    };
  }, [config.interval, shouldRotateTexts, texts.length]);

  useEffect(() => {
    if (texts.length > 0) {
      setCurrentTextIndex(Math.floor(Math.random() * texts.length));
    }
  }, [texts.length]);

  const LoadingLogo = config.loadingLogo;

  return (
    <ContextualLoadingContainer>
      <ContentWrapper>
        {typeof LoadingLogo === 'function' ? <LoadingLogo /> : LoadingLogo}

        <MessageTextContainer>
          {texts.length > 0 ? (
            <MessageText
              key={currentTextIndex}
              className={`fade-in-out ${isFadingOut ? 'fade-out' : ''}`.trim()}
            >
              {textToRender}
            </MessageText>
          ) : null}
        </MessageTextContainer>
      </ContentWrapper>
    </ContextualLoadingContainer>
  );
};

export { ContextualLoadingView };
